// ────────────────────────────────────────────────────────────────────
// File-to-asset picker — reuses the global search, prefilled with the tag the
// filename names (lib/assetTag), and badges an exact match as "Suggested".
// ────────────────────────────────────────────────────────────────────

import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { apiClient } from "@/lib/api";
import { guessTag } from "@/lib/assetTag";
import { norm } from "@/lib/intakeFiles";
import { PickerNote } from "@/components/intake/primitives";
import type { IntakeFile, SearchHit } from "@/lib/types";

// ── Asset picker (centered modal, reuses global search, auto-guesses tag) ──
export function AssetPickerModal({ files, onPick, onClose }: {
  files: IntakeFile[]; onPick: (assetId: number | null) => void; onClose: () => void;
}): JSX.Element {
  const single = files.length === 1 ? files[0] : null;
  const guess = single ? guessTag(single.filename) : "";
  const [q, setQ] = useState(guess);
  const [typed, setTyped] = useState(false);
  const sQ = useQuery({ queryKey: ["intake-asset-search", q], queryFn: () => apiClient.search(q), enabled: q.trim().length >= 1, staleTime: 15_000 });
  const hits: SearchHit[] = (sQ.data?.results ?? []).filter((h) => h.type === "asset");
  const suggestedId = useMemo(() => {
    if (!guess) return null;
    const g = norm(guess);
    return hits.find((h) => norm(h.label) === g)?.id ?? null;
  }, [hits, guess]);
  const ordered = useMemo(() => [...hits].sort((a, b) => (a.id === suggestedId ? -1 : b.id === suggestedId ? 1 : 0)), [hits, suggestedId]);
  const someFiled = files.some((f) => f.asset_id);
  // A prefilled guess that matches nothing must not read as "your search found
  // nothing" — the user never searched. Name the filename as the source of the
  // query and offer to drop it, so the box is one click from usable.
  const guessMissed = Boolean(guess) && !typed && q === guess && sQ.isSuccess && !sQ.isFetching && ordered.length === 0;
  return (
    <div className="fixed inset-0 z-[1200] flex items-start justify-center pt-[15vh]" style={{ background: "rgba(10,15,24,0.45)" }} onClick={onClose}>
      <div className="w-[480px] max-w-[92vw] shadow-2xl" onClick={(e) => e.stopPropagation()} style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }}>
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--hairline)" }}>
          <div className="font-mono uppercase truncate" style={{ fontSize: 10, letterSpacing: ".16em", color: "var(--ink-3)" }}>
            / FILE TO ASSET · <span style={{ color: "var(--ink-1)" }}>{single ? single.filename : `${files.length} files`}</span>
          </div>
          <button onClick={onClose}><X size={14} style={{ color: "var(--ink-3)" }} /></button>
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: "var(--hairline)" }}>
          <Search size={14} style={{ color: "var(--ink-4)" }} />
          <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setTyped(true); }} placeholder="Search assets by tag…"
                 className="flex-1 outline-none bg-transparent font-mono" style={{ fontSize: 13, color: "var(--ink-1)" }} />
        </div>
        <div className="max-h-[320px] overflow-auto">
          {q.trim().length < 1 && <PickerNote>Type a tag to find the destination asset</PickerNote>}
          {q.trim().length >= 1 && sQ.isFetching && <PickerNote>Searching…</PickerNote>}
          {guessMissed && (
            <PickerNote>
              No asset is called “{guess}” — that came from the filename, not from you.{" "}
              <button onClick={() => { setQ(""); setTyped(true); }} style={{ color: "var(--accent-deep)", textDecoration: "underline" }}>Clear</button>{" "}
              and search by tag.
            </PickerNote>
          )}
          {!guessMissed && q.trim().length >= 1 && !sQ.isFetching && ordered.length === 0 && <PickerNote>No assets found</PickerNote>}
          {ordered.map((h) => (
            <button key={h.id} onClick={() => onPick(h.id)} className="w-full text-left px-4 py-2.5 border-b hover:bg-[rgba(31,78,121,0.06)] transition-colors flex items-center justify-between gap-2" style={{ borderColor: "var(--hairline)" }}>
              <span className="min-w-0">
                <div className="font-mono" style={{ fontSize: 12.5, color: "var(--ink-1)", fontWeight: 600 }}>{h.label}</div>
                <div className="font-mono truncate" style={{ fontSize: 10, color: "var(--ink-4)" }}>{h.sublabel}</div>
              </span>
              {h.id === suggestedId && (
                <span className="font-mono uppercase shrink-0 px-1.5 py-0.5" style={{ fontSize: 8, letterSpacing: ".1em", color: "var(--accent-deep)", border: "1px solid var(--accent)" }}>Suggested</span>
              )}
            </button>
          ))}
        </div>
        {someFiled && (
          <button onClick={() => onPick(null)} className="w-full text-left px-4 py-2.5 font-mono hover:bg-[rgba(192,57,43,0.05)]" style={{ fontSize: 11, color: "var(--err)" }}>
            Remove from asset (back to Inbox)
          </button>
        )}
      </div>
    </div>
  );
}
